<template>
  <onboarding-layout>
    <template #header>
      <div class="back-link-container mb-4">
        <div class="d-flex align-center back-link" @click="$router.push('/register/integrations')">
          <i class="fas fa-arrow-left"></i>
          <span class="ml-2">{{ $t('goBack') }}</span>
        </div>
      </div>
      <v-img
        v-if="!isPluginPlatform && !isExnessPlatform && !showSyncStatus"
        :src="platform.logoUrl"
        :alt="platform.platformName || platform.brokerName"
        contain
        height="80"
        class="mb-4"
        max-width="160"
      ></v-img>
      <h2 v-if="!isPluginPlatform && !isExnessPlatform && !showSyncStatus" class="mb-2">{{ platform.platformName || platform.brokerName }}</h2>
      <p v-if="!isPluginPlatform && !isExnessPlatform && !showSyncStatus">
        {{ $t('connectPlatform', { name: platform.platformName || platform.brokerName }) }}
      </p>
    </template>

    <template #content>
      <template v-if="isExnessPlatform">
        <ConnectAccount 
          :isOnboarding="true" 
          @sync-success="handleSyncSuccess"
          @sync-error="handleSyncError"
          :platform-logo="platform.logoUrl"
        />
      </template>
      <template v-else-if="isMT5Platform && !mt5ManualImport">
        <ConnectMT5Account 
          :isOnboarding="true" 
          @sync-success="handleSyncSuccess"
          @sync-error="handleSyncError"
          :platform-logo="platform.logoUrl"
          :sync-loading="setSyncStatus"
        />
      </template>
      <template v-else-if="showSyncStatus">
        <SyncStatus
          :loading="syncLoading"
          :success="syncSuccess"
          :platform-logo="platform.logoUrl"
          :total-trades="totalTrades"
        />
      </template>
      <template v-else>
        <template v-if="isPluginPlatform">
          <PluginInstructions
            :platform-name="normalizedPlatformName"
            :platform-logo="platform.logoUrl"
            :download-link="platform.downloadLink"
          />
        </template>
        <template v-else>
          <v-row>
            <v-col cols="12" md="12" class="pa-0">
              <v-form ref="form" v-model="valid" class="mb-8" @input="validateForm">
                <v-row>
                  <v-col cols="12" md="8" class="px-0">
                    <v-select
                      v-if="hasAccountType"
                      :label="$t('form_PlatformSetup.accountType')"
                      :items="accountTypes"
                      v-model="formData.account_type"
                      :rules="fieldRules"
                      outlined
                      required
                      class="mb-4"
                      @blur="touchField('account_type')"
                      :error-messages="getFieldError('account_type')"
                    ></v-select>
                    <v-text-field
                      v-for="field in fieldsList"
                      :key="field"
                      :label="formatFieldLabel(field)"
                      v-model="formData[field]"
                      :type="field.includes('password') ? (showPassword[field] ? 'text' : 'password') : 'text'"
                      :rules="fieldRules"
                      outlined
                      required
                      class="mb-4"
                      @blur="touchField(field)"
                      :error-messages="getFieldError(field)"
                    >
                      <template v-if="field.includes('password')" v-slot:append>
                        <v-icon 
                          style="font-size: 16px;"
                          class="icon"
                          @click="togglePassword(field)"
                        >
                          {{ showPassword[field] ? 'fa-eye' : 'fa-eye-slash' }}
                        </v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>
              </v-form>
            </v-col>
          </v-row>
        </template>
      </template>
    </template>

    <template #actions >
      <!-- Only show these buttons if not showing ConnectAccount or PluginInstructions -->
      <v-btn
        v-if="(!isPluginPlatform && !isMT5Platform) && !isExnessPlatform && !showSyncStatus && !isMT5Platform"
        :disabled="!isFormValid"
        @click="connectPlatform"
      >
        {{ $t('connectButton') }}
      </v-btn><a class='label mt-4'
        @click="setMt5ManualImport"
        style="cursor: pointer; color: var(--v-mainGreen-base); position: absolute; top:8px; left: 200px; text-decoration: none;"
        v-if="(isMT5Platform && !mt5ManualImport && !showSyncStatus && !syncLoading)"
      >
        {{ $t('tryAnotherWay') }}
    </a>
    </template>

    <template #right-panel>
      <iframe
        style="width: 100%; height: 400px;"
        v-if="isPluginPlatform && platformVideoUrl && (!isMT5Platform ||  (isMT5Platform && mt5ManualImport))"
        :src="platformVideoUrl"
        frameborder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen
        class="tutorial-video"
      ></iframe>
      <lottie v-else :options="lottieOptions"/>
    </template>
  </onboarding-layout>
</template>

<script>
import * as animationData from '@/assets/animations/platforms.json';
import OnboardingLayout from '../components/OnboardingLayout.vue';
import ConnectAccount from '../../Exness/ConnectAccount.vue';
import ConnectMT5Account from '../../Metatrader/ConnectAccount.vue';
import SyncStatus from '../../../components/PlatformSync/SyncStatus.vue';
import PluginInstructions from '../../../components/PlatformSync/PluginInstructions.vue';
import BrokerApiCredentialRepo from '../../../repositories/brokerApiCredential';
import platformInstructions from '../../../config/platform-instructions.json';
import RepositoryBrokerAccount from '../../../repositories/brokerAccount';

export default {
  name: 'PlatformSetup',
  components: {
    OnboardingLayout,
    ConnectAccount,
    ConnectMT5Account,
    SyncStatus,
    PluginInstructions
  },
  data() {
    return {
      platform: {},
      valid: false,
      formData: {},
      isPluginPlatform: false,
      accountTypes: ['Demo', 'Live'],
      formValid: false,
      formErrors: [],
      touchedFields: {},
      lottieOptions: { 
        animationData: animationData.default,
        loop: true
      },
      fieldRules: [v => !!v && v.toString().trim() !== '' || this.$t('validations_PlatformSetup.fieldRequired')],
      formTouched: false,  // Add this property
      syncLoading: false,
      syncSuccess: false,
      showSyncStatus: false,
      totalTrades: 0,
      mt5ManualImport: false,
      showPassword: {}, // Add this new property
      brokers: []
    }
  },
  created() {
    // Guard: redirect to integrations if no platform data available
    if (!this.$route.params.platform) {
      this.$router.replace('/register/integrations');
      return;
    }

    try {
      this.platform = JSON.parse(this.$route.params.platform);
    } catch (e) {
      // Invalid JSON, redirect back
      this.$router.replace('/register/integrations');
      return;
    }

    // Ensure platform object is valid
    if (!this.platform || (!this.platform.platformName && !this.platform.brokerName)) {
      this.$router.replace('/register/integrations');
      return;
    }

    this.isPluginPlatform = this.platform.hasOwnProperty('downloadLink');

    // Redirect to manual import page if selected
    if (this.isManualImport) {
      this.$router.push('/journal/data'); // or wherever your manual import page is
    }
    this.loadBrokers();
    this.initializeFormData();
  },
  computed: {
    requiredFields() {
      if (this.isPluginPlatform) return ['automationToken'];
      return this.platform.resources ? this.platform.resources.split(', ') : [];
    },
    isExnessPlatform() {
      return this.platform.platformName?.toLowerCase().includes('exness');
    },
    isMT5Platform() {
      return this.platform.platformName === 'MetaTrader 5';
    },
    hasAccountType() {
      return this.platform.resources && this.platform.resources.includes('account_type');
    },
    fieldsList() {
      // Filter out account_type from required fields since it's handled separately
      return this.requiredFields.filter(field => field !== 'account_type');
    },
    isFormValid() {
      if (!this.formTouched) return false;  // Add this line
      return this.valid && this.formData && Object.keys(this.formData).length > 0 && 
          this.requiredFields.every(field => {
              const value = this.formData[field];
              return value !== undefined && value !== null && value.toString().trim() !== '';
          });
    },
    normalizedPlatformName() {
      // Ensure platform name matches JSON keys exactly
      return this.platform.platformName?.toUpperCase().trim().replace(' ', '') || '';
    },
    platformVideoUrl() {
      if (!this.isPluginPlatform) return null;
      // eslint-disable-next-line no-console
      console.log('normalizedPlatformName:', this.normalizedPlatformName);
      const platform = platformInstructions[this.normalizedPlatformName];
      const videoUrl = platform?.videos[this.$i18n.locale];
      
      if (!videoUrl) return null;

      const videoId = videoUrl.includes('youtu.be/') ? 
          videoUrl.split('/').pop() : 
          videoUrl.includes('watch?v=') ? 
              videoUrl.split('v=')[1] : 
              videoUrl;

      return `https://www.youtube.com/embed/${videoId}`;
    },
    isManualImport() {
      return this.platform.type === 'MANUAL_IMPORT';
    }
  },
  methods: {
    async loadBrokers() {
      await RepositoryBrokerAccount.getAllBrokers().then((response) => {
        if (response.data.length > 0)
                response.data.forEach((item) => {
                  if(item.name!=="")
                    this.brokers.push(item);
                });
      });
      this.brokers.sort((one, two) => (one.name > two.name) ? 1 : (one.name < two.name) ? -1 : 0);
    },
    initializeFormData() {
      this.formData = {
        account_type: this.hasAccountType ? 'Demo' : undefined
      };
      this.fieldsList.forEach(field => {
        this.formData[field] = '';
      });
      this.touchedFields = {};
      this.formTouched = false;  // Reset form touched state
    },
    formatFieldLabel(field) {
      return this.$t(`form_PlatformSetup.${field}`);
    },
    async connectPlatform() {
      this.syncLoading = true;
      this.showSyncStatus = true;

      try {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 3000));

        let apiCredential = {
          apiKey: this.formData.password,
          secret: this.formData.password,
          apiCredentialType: "FULL_ACCESS",
          userId: this.$store.state.user.id,
          brokerName: "X-TRADE BROKERS (XTB)",
          brokerAccountIdentifier: this.formData.account_id,
          accountType : this.formData.account_type.toUpperCase()
        };

        const response = await BrokerApiCredentialRepo.addApiCredential(apiCredential);
        
        if (response.status < 400) {
          this.syncSuccess = true;
          this.totalTrades = response.data;
        }
      } catch (error) {
        this.syncSuccess = false;
        // eslint-disable-next-line no-console
        console.error('Error connecting platform:', error);
      }
      
      this.syncLoading = false;
    },
    handleSyncSuccess(totalTrades) {
      this.syncSuccess = true;
      this.totalTrades = totalTrades;
      this.showSyncStatus = true;
      // Only redirect automatically on success
      setTimeout(() => {
        this.$router.push('/dashboard/', () => {
          this.$router.push('/journal');
        });
      }, 30000);
    },
    handleSyncError(error) {
      // eslint-disable-next-line no-console
      console.error('Platform sync error:', error);
      this.syncSuccess = false;
      this.showSyncStatus = true;
      // Remove the automatic redirect on error
      // Let the user click the "Back to Platforms" button manually
    },
    validateForm() {
      if (!this.formTouched) return;  // Add this line
      
      this.$refs.form.validate();
      this.formErrors = [];
      
      Object.keys(this.touchedFields).forEach(field => {
        if (this.touchedFields[field]) {
          const value = this.formData[field];
          if (!value || value.toString().trim() === '') {
            this.formErrors.push(`${this.formatFieldLabel(field)} is required`);
          }
        }
      });

      if (this.hasAccountType && this.touchedFields['account_type'] && !this.formData.account_type) {
        this.formErrors.push('Account type is required');
      }

      // Form is valid if all touched fields are valid
      this.valid = this.formErrors.length === 0 && 
                  Object.keys(this.touchedFields).length === this.requiredFields.length;
    },
    touchField(fieldName) {
      this.formTouched = true;  // Add this line
      this.$set(this.touchedFields, fieldName, true);
      this.validateForm();
    },
    getFieldError(fieldName) {
      if (!this.formTouched || !this.touchedFields[fieldName]) return [];  // Modify this line
      
      const value = this.formData[fieldName];
      if (!value || value.toString().trim() === '') {
        return [`${this.formatFieldLabel(fieldName)} ${this.$t('validations_PlatformSetup.fieldRequired').toLowerCase()}`];
      }
      return [];
    },
    togglePassword(field) {
      this.$set(this.showPassword, field, !this.showPassword[field]);
    },
    setMt5ManualImport() {
      this.mt5ManualImport = true;
    },
    setSyncStatus(value) {
      this.syncLoading = value;
    }
  },
  watch: {
    formData: {
      handler() {
        this.validateForm();
      },
      deep: true
    }
  }
}
</script>



<style lang="scss" scoped>
.v-btn.primary {
  background-color: var(--v-mainGreen-base) !important;
  color: var(--v-mainBlack-base) !important;
}

.back-link {
  cursor: pointer;
  color: #666666;
  font-size: 14px;
}

.back-link:hover {
  color: #333333;
}

.v-text-field {
  margin-top: 0;
  padding-top: 0;
}

.back-link {
  font-family: 'Avenir', sans-serif !important;
}

.v-text-field input {
  font-family: 'Avenir', sans-serif !important;
}

.content-indented {
  padding-left: 32px;
}

.error-text {
  color: var(---v-darkRed-base);
  font-size: 12px;
  margin-top: 4px;
}

.icon {
  border: none !important;
  cursor: pointer;
  color: var(--v-mainBlack-base);
  &:hover {
    background-color: transparent !important;
    border: none !important;
  }
}
</style>
